import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Building2,
  Users,
  CheckCircle2,
  Clock,
  Award,
  ClipboardCheck,
  TrendingUp,
  FileSpreadsheet,
  ArrowRight,
  ShieldCheck,
  AlertCircle,
  UserCheck
} from 'lucide-react';

export const SchoolDashboardPage: React.FC<{ onNavigate?: (tab: string) => void }> = ({ onNavigate }) => {
  const { school, fetchWithAuth } = useAuth();
  const [kpis, setKpis] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth('/api/school/dashboard-kpis');
      const json = await res.json();
      if (json.success) {
        setKpis(json.data);
      }
    } catch (e) {
      console.error('Failed to load school dashboard:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'APPROVED':
      case 'VERIFIED':
        return <span className="bg-emerald-100 text-emerald-800 font-bold px-2.5 py-1 rounded text-xs border border-emerald-300">Verified & Approved</span>;
      case 'SUBMITTED':
        return <span className="bg-blue-100 text-blue-800 font-bold px-2.5 py-1 rounded text-xs border border-blue-300">Submitted to BEO</span>;
      case 'DRAFT':
        return <span className="bg-amber-100 text-amber-800 font-bold px-2.5 py-1 rounded text-xs border border-amber-300">Draft In Progress</span>;
      default:
        return <span className="bg-rose-100 text-rose-800 font-bold px-2.5 py-1 rounded text-xs border border-rose-300">Pending Marks Entry</span>;
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* School Header Profile Card */}
      <div className="bg-white p-5 rounded-lg shadow-sm border border-slate-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-start space-x-3">
            <div className="w-12 h-12 rounded bg-[#0F2C59] text-amber-400 flex items-center justify-center font-bold text-lg shrink-0">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <div className="text-xs font-bold text-amber-600 uppercase tracking-wider">
                Government School Administration Dashboard
              </div>
              <h1 className="text-lg md:text-xl font-bold text-[#0F2C59] mt-0.5">
                {kpis?.school_name || school?.school_name || 'Government Senior Secondary School, Nuh'}
              </h1>
              <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>Code: <strong className="text-slate-700">{kpis?.school_code || school?.school_code || 'HR-NUH-0001'}</strong></span>
                <span>•</span>
                <span>Block: <strong className="text-slate-700">{kpis?.block || 'Nuh'}</strong></span>
                <span>•</span>
                <span>Category: <strong className="text-slate-700">{kpis?.category || 'Senior Secondary'}</strong></span>
                <span>•</span>
                <span>Session: <strong className="text-slate-700">2026-27</strong></span>
              </div>
            </div>
          </div>

          <div className="bg-slate-50 p-3 rounded border border-slate-200 text-right">
            <div className="text-[11px] text-slate-500 font-semibold">Current Month Status (August)</div>
            <div className="mt-1">{getStatusBadge(kpis?.current_assessment_status || 'SUBMITTED')}</div>
          </div>
        </div>
      </div>

      {/* Primary KPI Cards — Active vs Enrolled Distinction */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {/* 1. Current Active Students (Primary KPI) */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 rounded-xl border border-emerald-200 shadow-xs col-span-2 sm:col-span-2">
          <div className="flex justify-between items-center text-emerald-800 text-xs font-bold uppercase tracking-wider">
            <span>Current Active Students</span>
            <Users className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="text-3xl font-black text-emerald-900 mt-2">
            {(kpis?.current_active_students || kpis?.total_students || 0).toLocaleString('en-IN')}
          </div>
          <div className="text-xs text-emerald-700 font-medium mt-1 flex items-center gap-3">
            <span>Boys: <strong>{kpis?.active_boys || 0}</strong></span>
            <span>•</span>
            <span>Girls: <strong>{kpis?.active_girls || 0}</strong></span>
            <span>•</span>
            <span>Class 10: <strong>{kpis?.class_10_students || 0}</strong></span>
            <span>•</span>
            <span>Class 12: <strong>{kpis?.class_12_students || 0}</strong></span>
          </div>
        </div>

        {/* 2. Total Enrolled */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <div className="flex justify-between items-center text-slate-500 text-xs font-bold uppercase">
            <span>Total Enrolled</span>
            <Building2 className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-2">
            {(kpis?.total_enrolled_students || 0).toLocaleString('en-IN')}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">Master Records</div>
        </div>

        {/* 3. Transferred / Left School */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <div className="flex justify-between items-center text-slate-500 text-xs font-bold uppercase">
            <span>Transferred / Left</span>
            <AlertCircle className="w-4 h-4 text-rose-500" />
          </div>
          <div className="text-2xl font-bold text-rose-700 mt-2">
            {(kpis?.transferred_students || 0) + (kpis?.left_school_students || 0)}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            {kpis?.transferred_students || 0} Transferred • {kpis?.left_school_students || 0} Left
          </div>
        </div>

        {/* 4. Current Month Avg Score */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <div className="flex justify-between items-center text-slate-500 text-xs font-bold uppercase">
            <span>August Avg Score</span>
            <TrendingUp className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-2xl font-bold text-emerald-700 mt-2">
            {kpis?.current_average_score !== undefined ? `${kpis.current_average_score}%` : '0.0%'}
          </div>
          <div className="text-[11px] text-slate-500 font-medium mt-1">From Active Marks</div>
        </div>

        {/* 5. Pending Entries */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <div className="flex justify-between items-center text-slate-500 text-xs font-bold uppercase">
            <span>Pending Entries</span>
            <Clock className="w-4 h-4 text-amber-600" />
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-2">
            {kpis?.pending_entries_count || 0}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">To evaluate</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-5 rounded-lg shadow-sm border border-slate-200">
        <h2 className="text-sm font-bold text-[#0F2C59] uppercase tracking-wider mb-4">
          Quick Administrative Actions
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <button
            onClick={() => onNavigate && onNavigate('daily-attendance')}
            className="p-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded-lg text-left transition flex items-start justify-between group shadow-xs"
          >
            <div>
              <UserCheck className="w-6 h-6 text-emerald-700 mb-2" />
              <div className="font-bold text-slate-900 text-sm flex items-center gap-1.5">
                <span>Daily Attendance</span>
                <span className="text-[10px] bg-emerald-700 text-white px-1.5 py-0.5 rounded font-bold">6–12</span>
              </div>
              <div className="text-xs text-slate-600 mt-0.5">Submit daily student & faculty attendance</div>
            </div>
            <ArrowRight className="w-4 h-4 text-emerald-700 group-hover:translate-x-1 transition" />
          </button>

          <button
            onClick={() => onNavigate && onNavigate('student-lifecycle')}
            className="p-4 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg text-left transition flex items-start justify-between group"
          >
            <div>
              <Users className="w-6 h-6 text-teal-700 mb-2" />
              <div className="font-bold text-slate-900 text-sm">Student Status & Transfers</div>
              <div className="text-xs text-slate-600 mt-0.5">Verify active students, record transfers & exits</div>
            </div>
            <ArrowRight className="w-4 h-4 text-teal-700 group-hover:translate-x-1 transition" />
          </button>

          <button
            onClick={() => onNavigate && onNavigate('monthly-assessment')}
            className="p-4 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-left transition flex items-start justify-between group"
          >
            <div>
              <ClipboardCheck className="w-6 h-6 text-blue-700 mb-2" />
              <div className="font-bold text-slate-900 text-sm">Enter Monthly Marks</div>
              <div className="text-xs text-slate-600 mt-0.5">Submit August marks for Class 10 & 12</div>
            </div>
            <ArrowRight className="w-4 h-4 text-blue-700 group-hover:translate-x-1 transition" />
          </button>

          <button
            onClick={() => onNavigate && onNavigate('sat-marks')}
            className="p-4 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg text-left transition flex items-start justify-between group"
          >
            <div>
              <Award className="w-6 h-6 text-indigo-700 mb-2" />
              <div className="font-bold text-slate-900 text-sm">SAT Marks Registry</div>
              <div className="text-xs text-slate-600 mt-0.5">Enter SAT 1 & SAT 2 marks</div>
            </div>
            <ArrowRight className="w-4 h-4 text-indigo-700 group-hover:translate-x-1 transition" />
          </button>

          <button
            onClick={() => onNavigate && onNavigate('reports')}
            className="p-4 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg text-left transition flex items-start justify-between group"
          >
            <div>
              <FileSpreadsheet className="w-6 h-6 text-amber-700 mb-2" />
              <div className="font-bold text-slate-900 text-sm">School Reports</div>
              <div className="text-xs text-slate-600 mt-0.5">Download CSV & PDF scorecards</div>
            </div>
            <ArrowRight className="w-4 h-4 text-amber-700 group-hover:translate-x-1 transition" />
          </button>
        </div>
      </div>
    </div>
  );
};
